import { Button, IconButton } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Icon } from '@chakra-ui/icon';
import { Flex, Box, Text, Heading, Stack, Badge } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import { DotsIcons, MainButton, SearchIcon } from './UiElements';
import {
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    ButtonGroup,
    Image
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import layoutStore from '../stores/layoutStore';
import useStore from '../stores/store';
import { Portal } from '@chakra-ui/portal';
import UserPopover from './UserPopover';
import SettingsDrawer from './SettingsDrawer';
import { AnimatePresence, motion } from 'framer-motion';
import RemindersButton from './Reminders';
import { scrollToGroupContainer } from './Groups';
import { PLATFORM } from '../../platformFiles/utils/platformConstants';
import useCustomToast from './ToastProvider';

const TopRightOptions = () => {
    const { colorMode, toggleColorMode } = useColorMode();
    const setBookmarksModal = layoutStore((state) => state.setBookmarksModal);
    const setShowShareModal = layoutStore((state) => state.setShowShareModal);
    const setShowSearchModal = layoutStore((state) => state.setShowSearchModal);

    // const hoverSide = layoutStore((state) => state.hoverSide);
    const text = useColorModeValue('#1A202C', '#FFF');
    const borderBottom = useColorModeValue(
        '1px solid #e4eaf0',
        '1px solid #394146'
    );
    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const pickerBg = useColorModeValue('rgba(244, 245, 247, 1)', '#212528');
    const lightBg = useColorModeValue('gray.50', '#31363a');
    const menuProps = {
        bg: { menuBg },
        p: '8px 16px 8px 8px',
        rounded: '8px',
        fontWeight: 600,
        transition: '0.1s',
        //margin: '4px',
        _hover: { bg: lightBg }
    };

    const createGroup = (e) => {
        e.preventDefault();
        useStore.getState().addGroup();
        scrollToGroupContainer('end');
    };

    const showToast = useCustomToast();
    //Bookmarks stuff
    const openBookmarksModal = () => {
        if (PLATFORM === 'web') {
            showToast({
                title: 'Not available in web version',
                subtitle:
                    'Please add the browser extension to access your bookmarks.',
                type: 'info'
            });
        } else {
            setBookmarksModal(true);
        }
    };

    const openFeedbackModal = () => {
        layoutStore.getState().setShowFeedbackModal(true);
    };

    const openShareModal = () => {
        setShowShareModal(true, useStore.getState().currentWsId);
    };
    const openBinDrawer = () => {
        layoutStore.getState().setShowBinDrawer(true);
    };
    //Settings stuff
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [hoverItem, setHoverItem] = useState(null);

    const isMac = navigator?.userAgent?.indexOf('Mac') !== -1;
    const cmd = isMac ? '⌘' : 'Ctrl';
    ///^

    return (
        <Flex
            ml="auto"
            mr={{ base: 0, md: '24px' }}
            pos={{ base: 'static', md: 'fixed' }}
            right={{ base: 'auto', md: '8px' }}
            top={{ base: 'auto', md: '22px' }}
            order={{ base: 2, md: 3 }}
            flexShrink={0}
            zIndex={2}
            // opacity={hoverSide ? 0 : 1}
            transition={'0.2s'}
        >
            <IconButton
                onClick={() => setShowSearchModal(true)}
                aria-label="Search"
                icon={
                    <SearchIcon
                        color={'var(--chakra-colors-iconMd)'}
                        size={'18px'}
                    />
                }
                bg="transparent"
                h="32px"
                w="32px"
                minW="32px"
                rounded="8px"
                p="0"
                alignItems={'center'}
                transition={'0.1s'}
                mr="4px"
                _active={{
                    transform: 'scale(0.925)'
                }}
            />
            <Menu isLazy>
                {({ isOpen }) => (
                    <>
                        <MenuButton
                            as={IconButton}
                            aria-label="Options"
                            icon={<DotsIcons />}
                            bg="transparent"
                            h="32px"
                            w="32px"
                            minW="32px"
                            rounded="8px"
                            p="0"
                            alignItems={'center'}
                            transition={'0.1s'}
                            mr="12px"
                            _active={{
                                transform: 'scale(0.925)'
                            }}
                        />
                        <AnimatePresence>
                            {isOpen && (
                                <MenuList
                                    as={motion.div}
                                    initial={{ y: -8, opacity: 0.4 }}
                                    animate={{
                                        y: 0,
                                        opacity: 1,
                                        transition: {
                                            duration: 0.12,
                                            ease: [0.17, 0.67, 0.83, 0.67]
                                        }
                                    }}
                                    bg={menuBg}
                                    fontSize={'14px'}
                                    p="4px 4px 0"
                                    border="none"
                                    rounded="12px"
                                    boxShadow={
                                        'var(--chakra-colors-popoverShadowLight)'
                                    }
                                    minW="284px"
                                >
                                    <MenuItem
                                        icon={
                                            <ImageIcon
                                                name="new_group"
                                                hoverItem={hoverItem}
                                            />
                                        }
                                        command={cmd + 'G'}
                                        {...menuProps}
                                        onClick={createGroup}
                                        onMouseEnter={() =>
                                            setHoverItem('new_group')
                                        }
                                        onMouseLeave={() => setHoverItem(null)}
                                    >
                                        New group
                                    </MenuItem>
                                    <MenuItem
                                        icon={
                                            <ImageIcon
                                                name="bookmarks"
                                                hoverItem={hoverItem}
                                            />
                                        }
                                        onClick={openBookmarksModal}
                                        {...menuProps}
                                        onMouseEnter={() =>
                                            setHoverItem('bookmarks')
                                        }
                                        onMouseLeave={() => setHoverItem(null)}
                                        opacity={
                                            PLATFORM === 'web' ? 0.4625 : null
                                        }
                                        style={{
                                            filter:
                                                PLATFORM === 'web'
                                                    ? 'grayscale(1)'
                                                    : null
                                        }}
                                    >
                                        Import bookmarks
                                    </MenuItem>
                                    <MenuItem
                                        icon={
                                            <ImageIcon
                                                name="feedback"
                                                hoverItem={hoverItem}
                                            />
                                        }
                                        {...menuProps}
                                        onClick={openFeedbackModal}
                                        onMouseEnter={() =>
                                            setHoverItem('feedback')
                                        }
                                        onMouseLeave={() => setHoverItem(null)}
                                    >
                                        Feedback
                                    </MenuItem>
                                    <MenuItem
                                        icon={
                                            <ImageIcon
                                                name="settings"
                                                hoverItem={hoverItem}
                                            />
                                        }
                                        {...menuProps}
                                        onClick={onOpen}
                                        onMouseEnter={() =>
                                            setHoverItem('settings')
                                        }
                                        onMouseLeave={() => setHoverItem(null)}
                                    >
                                        Settings
                                    </MenuItem>
                                    <MenuItem
                                        icon={
                                            <ImageIcon
                                                name="bin"
                                                hoverItem={hoverItem}
                                            />
                                        }
                                        {...menuProps}
                                        onClick={openBinDrawer}
                                        onMouseEnter={() => setHoverItem('bin')}
                                        onMouseLeave={() => setHoverItem(null)}
                                        command={cmd + 'Z'}
                                    >
                                        Bin
                                    </MenuItem>
                                    <Box
                                        borderTop={borderBottom}
                                        bg={lightBg}
                                        p="8px 18px"
                                        borderRadius={'0 0 12px 12px'}
                                        mx="-4px"
                                        mt="4px"
                                    >
                                        <ThemeSelector />
                                    </Box>
                                </MenuList>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </Menu>
            <Drawer
                isOpen={isOpen}
                placement="right"
                onClose={onClose}
                size="sm"
            >
                <DrawerOverlay
                    style={{ filter: 'blur(1px)' }}
                    cursor={'pointer'}
                />
                <DrawerContent bg={pickerBg}>
                    <DrawerCloseButton
                        size="sm"
                        opacity={0.6}
                        rounded="8px"
                        mt="4px"
                    />
                    <DrawerHeader mt="16px" pb="12px" px="20px">
                        Settings
                    </DrawerHeader>
                    <DrawerBody p="0" overflow={'hidden'}>
                        <SettingsDrawer onClose={onClose} />
                    </DrawerBody>
                    <DrawerFooter>
                        <Button
                            color="#FFF"
                            onClick={onClose}
                            bg="linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)"
                            boxShadow={
                                '0 2px 3px -3px #287AD5, rgba(70, 94, 193, 0.25) -1px -1px 1px 0px inset'
                            }
                            _hover={{ bg: '', filter: 'brightness(1.05)' }}
                            _active={{ bg: '', filter: 'brightness(0.98)' }}
                            transition={'0.1s'}
                            rounded="8px"
                        >
                            Done
                            <ArrowRight />
                        </Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>
            <RemindersButton />
            <MainButton icon={'share'} onClick={openShareModal} mr="12px" />
            <UserPopover />
        </Flex>
    );
};

export const ImageIcon = ({ name, hoverItem, mr = '0px' }) => {
    const path = {
        new_group: '../../../assets/images/newgroup_gradient.png',
        bookmarks: '../../../assets/images/bookmarks_gradient.png',
        settings: '../../../assets/images/settings_gradient.png',
        bin: '../../../assets/images/trash_gradient.png',
        save_close: '../../../assets/images/save_close.png',
        copy_tabs: '../../../assets/images/copy_tabs.png',
        clear_tabs: '../../../assets/images/clear_tabs.png',
        feedback: '../../../assets/images/feedback_gradient.png'
    };

    // const iconHoverShadow = {
    //     light: 'brightness(1.035) drop-shadow(0px 4px 4px rgb(34, 46, 69, 0.24))',
    //     dark: 'brightness(1.025) drop-shadow(0px 4px 6px rgb(0, 0, 0, 0.4))'
    // };
    const iconHoverShadow = useColorModeValue(
        'brightness(1.035) drop-shadow(0px 3px 3px rgb(34, 46, 69, 0.18))',
        'brightness(1.025) drop-shadow(0px 2px 5px rgb(0, 0, 0, 0.54))'
    );

    return (
        <Image
            src={path[name]}
            alt={name + 'image'}
            height="32px"
            width="32px"
            filter={hoverItem === name ? iconHoverShadow : ''}
            transition={'0.3s'}
            mr={mr}
        />
    );
};

export default TopRightOptions;

const ArrowRight = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="#FFF"
            style={{ width: '16px', height: '16px', marginLeft: '4px' }}
        >
            <path
                fillRule="evenodd"
                d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const ThemeColor = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const fxStateZu = (state) => state.fxState;

export const ThemeSelector = ({ withTitles = false }) => {
    const { colorMode, toggleColorMode } = useColorMode();
    //const text = { light: '#1A202C', dark: '#FFF' };
    //const bg = { light: 'gray.200', dark: '#FFFFFF29' };
    const text = useColorModeValue('#1A202C', '#FFF');
    const bg = useColorModeValue('gray.200', '#FFFFFF29');
    const [value, setValue] = useState(null);

    useEffect(() => {
        let themeState = localStorage.getItem('themeState');
        if (themeState === 'light') {
            setValue('1');
        } else if (themeState === 'dark') {
            setValue('2');
        } else if (themeState === 'auto') {
            setValue('3');
            checkCurrentMode(); // Apply auto theme on initialization
        }
    }, []);

    const setDarkMode = (value) => {
        setValue(value);
        if (value === '1') {
            localStorage.setItem('themeState', 'light');
            if (colorMode === 'dark') {
                toggleColorMode();
            }
        }
        if (value === '2') {
            localStorage.setItem('themeState', 'dark');
            if (colorMode === 'light') {
                toggleColorMode();
            }
        }
        if (value === '3') {
            localStorage.setItem('themeState', 'auto');
            checkCurrentMode();
        }
    };

    const checkCurrentMode = () => {
        if (
            window.matchMedia &&
            window.matchMedia('(prefers-color-scheme: dark)').matches
        ) {
            if (colorMode === 'light') {
                toggleColorMode();
            } else return;
        } else {
            if (colorMode === 'dark') {
                toggleColorMode();
            } else return;
        }
    };

    return (
        <Flex alignItems="center" my="6px">
            <Icon
                as={ThemeColor}
                color={colorMode === 'light' ? 'gray.600' : '#FFF'}
                boxSize="20px"
                mr="16px"
                //mt="-4px"
            />
            <Text
                ml="8px"
                color={text}
                fontSize="14px"
                fontWeight={500}
                userSelect={'none'}
                //mt="-4px"
            >
                Theme
            </Text>
            <ButtonGroup spacing={2} ml="auto">
                <Button
                    size={withTitles ? 'sm' : 'xs'}
                    bg={bg}
                    color={text}
                    transition="0.1s"
                    onClick={() => setDarkMode('1')}
                    boxShadow={
                        value === '1' ? '0px 0px 0px 2px #4299E1' : 'none'
                    }
                    minH="30px"
                    minW="32px"
                    rounded="8px"
                    p={withTitles ? '' : '0'}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill={colorMode === 'light' ? '#4A5568' : '#FFE300'}
                        style={{ width: '14px', height: '14px' }}
                    >
                        <path
                            fillRule="evenodd"
                            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                            clipRule="evenodd"
                        />
                    </svg>
                </Button>
                <Button
                    size={withTitles ? 'sm' : 'xs'}
                    bg={bg}
                    color={text}
                    transition="0.1s"
                    onClick={() => setDarkMode('2')}
                    boxShadow={
                        value === '2' ? '0px 0px 0px 2px #4299E1' : 'none'
                    }
                    p={withTitles ? '' : '0'}
                    minH="30px"
                    minW="32px"
                    rounded="8px"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill={colorMode === 'light' ? '#4A5568' : '#FFE300'}
                        style={{ width: '14px', height: '14px' }}
                    >
                        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                    </svg>
                </Button>
                <Button
                    size={withTitles ? 'sm' : 'xs'}
                    bg={bg}
                    color={text}
                    transition="0.1s"
                    onClick={() => setDarkMode('3')}
                    boxShadow={
                        value === '3' ? '0px 0px 0px 2px #4299E1' : 'none'
                    }
                    lineHeight={'22px'}
                    minH="30px"
                    rounded="8px"
                >
                    Auto
                </Button>
            </ButtonGroup>
        </Flex>
    );
};

const SettingsIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
        </svg>
    );
};
const BookmarksIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
        </svg>
    );
};
const AddGroupIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1H8a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V9z"
            />
        </svg>
    );
};
